import { useEffect, useState } from 'react';
import { useInView } from '@/hooks/useInView';
import HeroBackground from '@/components/HeroBackground';

// const HERO_PHOTO = 'https://customer-assets.emergentagent.com/job_artist-epk-premium/artifacts/2xo86gdn_Gemini_Generated_Image_s2w1pms2w1pms2w1.png';
const HERO_PHOTO = 'hero-image.png';

const TAGLINE =
  'From township imagination to global dance floors — a cinematic Afro-electronic world shaped by rhythm, soul, and story.';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 60;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

// Splits text into word spans with staggered transition delays — each word reveals independently.
function SplitWords({ text, visible, base = 0.3, step = 0.04, className = '' }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, i) => (
        <span
          key={`${word}-${i}`}
          className={`split-word${visible ? ' visible' : ''} ${className}`.trim()}
          style={{ transitionDelay: `${(base + i * step).toFixed(3)}s` }}
        >
          {word}
          {i < words.length - 1 ? '\u00A0' : ''}
        </span>
      ))}
    </>
  );
}

// Live Amsterdam time (CET/CEST auto-handled by Intl).
function formatAmsterdam() {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date());
  } catch {
    return '--:--';
  }
}

function useAmsterdamTime() {
  const [time, setTime] = useState(formatAmsterdam);
  useEffect(() => {
    const tick = () => setTime(formatAmsterdam());
    tick();
    const id = setInterval(tick, 30000); // update every 30s
    return () => clearInterval(id);
  }, []);
  return time;
}

export default function Hero() {
  const [ref, visible] = useInView();
  const amsterdamTime = useAmsterdamTime();

  return (
    <section id="hero" className="hero" data-testid="hero-section">
      <div
        className="hero-bg"
        style={{ backgroundImage: `url(${HERO_PHOTO})` }}
        aria-hidden="true"
      />
      <HeroBackground src={HERO_PHOTO} />
      <div className="hero-overlay" aria-hidden="true" />
      <div className="hero-texture" aria-hidden="true" />

      {/* Editorial meta stamp — live Amsterdam clock + booking status */}
      <div
        className={`hero-meta reveal${visible ? ' visible' : ''}`}
        data-testid="hero-meta"
      >
        <span className="hero-meta-item">
          <span className="hero-meta-label">Amsterdam</span>
          <span className="hero-meta-value">{amsterdamTime} CET</span>
        </span>
        <span className="hero-meta-divider" aria-hidden="true" />
        <span className="hero-meta-item">
          <span className="hero-meta-label">Bookings</span>
          <span className="hero-meta-value">Open · Late May&nbsp;&rsquo;26</span>
        </span>
      </div>

      <div ref={ref} className="hero-content">
        <div
          className={`hero-eyebrow reveal${visible ? ' visible' : ''}`}
          data-testid="hero-eyebrow"
        >
          South African DJ &amp; Producer
        </div>

        <h1 className="hero-name display" data-testid="hero-name">
          <span
            className={`split-word${visible ? ' visible' : ''}`}
            style={{ transitionDelay: '0.15s' }}
          >
            Lulo
          </span>
          <br />
          <span
            className={`split-word${visible ? ' visible' : ''}`}
            style={{ transitionDelay: '0.32s' }}
          >
            <em>Lonke</em>
          </span>
        </h1>

        <p className="hero-tagline" data-testid="hero-tagline">
          <SplitWords text={TAGLINE} visible={visible} base={0.55} step={0.035} />
        </p>

        <div
          className={`hero-ctas reveal${visible ? ' visible' : ''}`}
          style={{ transitionDelay: '1.4s' }}
          data-testid="hero-ctas"
        >
          <a
            href="#sound"
            className="btn-primary"
            onClick={(e) => {
              e.preventDefault();
              scrollTo('sound');
            }}
            data-testid="btn-listen"
          >
            Listen
          </a>
          <a
            href="#contact"
            className="btn-outline"
            onClick={(e) => {
              e.preventDefault();
              scrollTo('contact');
            }}
            data-testid="btn-bookings"
          >
            Bookings
          </a>
        </div>
      </div>

      <div className="hero-scroll-hint" aria-hidden="true">
        <div className="scroll-line" />
        <span>Scroll</span>
      </div>
    </section>
  );
}
